import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ImageIcon, Loader2, RefreshCw, Send, X } from 'lucide-react';
import {
    camposDoTemplate,
    escolherTemplate,
    preencherTemplate,
    renderizarSite,
    templatePorId,
    TEMPLATES,
    valoresDoCliente,
    type CampoTemplate,
    type ValoresTemplate,
} from '@nyroh/contracts';

import { api, CDN_ASSETS, enviarImagemComChave } from '../lib/api';
import { BibliotecaDeImagens } from './BibliotecaDeImagens';
import { enfileirarUmCliente } from '../lib/esteira-fila';

/**
 * ============================================================================
 * EDITOR DE TEMPLATE — preencher os `{{ }}` sem escrever HTML
 * ============================================================================
 *
 * O QUE FALTAVA
 *
 * A fábrica só tinha dois extremos: a IA escrevia o site inteiro, ou não havia
 * site. Não existia o meio — alguém olhar o que saiu, trocar um texto, subir uma
 * foto e publicar. Com a IA fora do ar, o meio virou o único caminho possível.
 *
 * COMO ELE COMEÇA
 *
 * **Preenchido.** Nome, telefone, endereço, categoria, resumo e as palavras-chave
 * já estão no banco — vieram da captação. `valoresDoCliente` os transforma nos
 * campos do formulário, e o operador corrige em vez de digitar do zero. Fazer
 * alguém redigitar o que o sistema já sabe é o tipo de trabalho que faz a
 * ferramenta não ser usada.
 *
 * A PRÉVIA É O SITE
 *
 * O `<iframe>` mostra `renderizarSite(preencherTemplate(...))` — a MESMA função
 * que o worker roda ao publicar. Não é uma aproximação: os bytes da prévia são
 * os bytes que vão para o R2.
 */

interface Props {
    clienteId: string;
    onFechar: () => void;
    /** Chamado depois de publicar, para a tela de trás recarregar. */
    onPublicado?: () => void;
}

type ClienteDaApi = Awaited<ReturnType<typeof api.chamar<'clientes.obter'>>>;

export const EditorDeTemplate: React.FC<Props> = ({ clienteId, onFechar, onPublicado }) => {
    const [cliente, setCliente] = useState<ClienteDaApi | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    const [templateId, setTemplateId] = useState<string>('');
    const [valores, setValores] = useState<ValoresTemplate>({});
    const [publicando, setPublicando] = useState(false);
    const [publicado, setPublicado] = useState(false);

    /** Qual campo de imagem o seletor de arquivo está servindo. */
    const [campoDeImagem, setCampoDeImagem] = useState<string | null>(null);
    /** Campo que a biblioteca vai preencher. `''` = aberta so para administrar. */
    const [bibliotecaPara, setBibliotecaPara] = useState<string | null>(null);
    const [enviando, setEnviando] = useState<string | null>(null);
    const arquivoRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let cancelado = false;
        void (async () => {
            try {
                const c = await api.chamar('clientes.obter', { params: { id: clienteId } });
                if (cancelado) return;
                setCliente(c);
                // Escolha automática pela categoria — a mesma do worker, então o
                // que a tela mostra é o que sairia sem ninguém tocar em nada.
                setTemplateId(escolherTemplate(c.categoria, c.nome).id);
                setValores(valoresDoCliente(c));
            } catch (e) {
                if (!cancelado) setErro(e instanceof Error ? e.message : 'Não consegui carregar o cliente.');
            } finally {
                if (!cancelado) setCarregando(false);
            }
        })();
        return () => { cancelado = true; };
    }, [clienteId]);

    const template = useMemo(
        () => templatePorId(templateId) ?? TEMPLATES[TEMPLATES.length - 1]!,
        [templateId],
    );
    const campos = useMemo(() => camposDoTemplate(template), [template]);

    /**
     * A prévia.
     *
     * Recalculada a cada tecla. É barato: `preencherTemplate` e `renderizarSite`
     * são funções puras sobre um objeto pequeno — nada de rede, nada de IA.
     */
    const html = useMemo(() => {
        try {
            return renderizarSite(preencherTemplate(template, valores), { baseCdn: CDN_ASSETS });
        } catch (e) {
            console.error('[editor-template] falha ao renderizar a prévia:', e);
            return '<p style="font:14px system-ui;padding:24px">Não consegui montar a prévia.</p>';
        }
    }, [template, valores]);

    const definir = (chave: string, valor: string) =>
        setValores((v) => ({ ...v, [chave]: valor }));

    const abrirSeletor = (chave: string) => {
        setCampoDeImagem(chave);
        arquivoRef.current?.click();
    };

    const aoEscolherArquivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = e.target.files?.[0];
        const chave = campoDeImagem;
        if (!arquivo || !chave) return;

        setEnviando(chave);
        try {
            // Os bytes vão DIRETO para o R2, com URL pré-assinada — não passam
            // pela API. E o que guardamos é a chave do objeto, não a URL: é ela
            // que o spec entende, e é o que permite trocar o CDN depois.
            const { chaveR2 } = await enviarImagemComChave(arquivo, { clienteId });
            definir(chave, chaveR2);
        } catch (err) {
            console.error('[editor-template] falha no upload:', err);
            alert(err instanceof Error ? err.message : 'Não consegui subir a imagem.');
        } finally {
            setEnviando(null);
            setCampoDeImagem(null);
            if (arquivoRef.current) arquivoRef.current.value = '';
        }
    };

    const publicar = async () => {
        setPublicando(true);
        try {
            const spec = preencherTemplate(template, valores);
            const sites = await api.chamar('sites.listarPorCliente', { params: { id: clienteId } });
            const site =
                sites[0] ??
                (await api.chamar('sites.criar', {
                    corpo: { clienteId, slug: cliente!.slug, spec },
                }));

            await api.chamar('sites.publicar', {
                params: { id: site.id },
                corpo: { spec, geradoPor: `template:${template.id}`, apenasRascunho: false },
            });
            setPublicado(true);
            onPublicado?.();
        } catch (e) {
            console.error('[editor-template] falha ao publicar:', e);
            alert(e instanceof Error ? e.message : 'Não consegui publicar.');
        } finally {
            setPublicando(false);
        }
    };

    const enfileirar = async () => {
        setPublicando(true);
        try {
            const novo = await enfileirarUmCliente(clienteId, template.id);
            alert(
                novo
                    ? 'Na fila do worker. Pode fechar esta tela.\n\nAtenção: a fila usa os dados do CADASTRO, não o que você editou aqui — para publicar suas edições, use "Publicar agora".'
                    : 'Este cliente já estava na fila.',
            );
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Não consegui enfileirar.');
        } finally {
            setPublicando(false);
        }
    };

    if (carregando) {
        return (
            <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
                <Loader2 className="animate-spin text-slate-400" size={36} />
            </div>
        );
    }

    if (erro || !cliente) {
        return (
            <div className="fixed inset-0 z-50 bg-white flex items-center justify-center p-6">
                <div className="max-w-sm text-center">
                    <p className="text-sm text-red-700 font-semibold">Não consegui abrir o editor</p>
                    <p className="text-xs text-slate-500 mt-2">{erro}</p>
                    <button onClick={onFechar} className="mt-4 px-4 py-2 text-xs font-bold bg-slate-900 text-white rounded-sm">
                        Fechar
                    </button>
                </div>
            </div>
        );
    }

    const rotuloCampo = (c: CampoTemplate) => (
        <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">
            {c.rotulo}
            {c.ajuda && <span className="ml-2 normal-case tracking-normal font-normal text-slate-400">{c.ajuda}</span>}
        </label>
    );

    const estiloInput =
        'w-full border border-slate-200 rounded-sm px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-900 transition-colors';

    return (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
            <header className="h-14 border-b border-slate-200 flex items-center justify-between px-5 shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                    <button onClick={onFechar} className="text-slate-400 hover:text-slate-900" title="Fechar">
                        <X size={18} />
                    </button>
                    <h1 className="font-bold text-sm text-slate-900 truncate">{cliente.nome}</h1>
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 shrink-0">
                        editor de template
                    </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={enfileirar}
                        disabled={publicando}
                        className="px-4 py-2 text-xs font-bold border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-50"
                        title="Deixa o worker publicar. Usa os dados do cadastro, não as edições desta tela."
                    >
                        <Send size={13} className="inline mr-1.5" />
                        Mandar para a fila
                    </button>
                    <button
                        onClick={publicar}
                        disabled={publicando}
                        className="px-5 py-2 text-xs font-bold bg-slate-900 text-white rounded-sm hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
                    >
                        {publicando ? <Loader2 size={13} className="animate-spin" /> : publicado ? <Check size={13} /> : null}
                        {publicado ? 'Publicado' : 'Publicar agora'}
                    </button>
                </div>
            </header>

            <div className="flex-1 flex min-h-0">
                {/* ------------------------------------------------ formulário --- */}
                <div className="w-[420px] border-r border-slate-200 overflow-y-auto shrink-0">
                    <div className="p-5 border-b border-slate-100">
                        {rotuloCampo({ chave: 'template', rotulo: 'Modelo', tipo: 'curto' })}
                        <select
                            value={templateId}
                            onChange={(e) => setTemplateId(e.target.value)}
                            className={estiloInput}
                        >
                            {TEMPLATES.map((t) => (
                                <option key={t.id} value={t.id}>{t.nome}</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-slate-400 mt-1.5">{template.descricao}</p>
                        <button
                            onClick={() => {
                                setTemplateId(escolherTemplate(cliente.categoria, cliente.nome).id);
                                setValores(valoresDoCliente(cliente));
                            }}
                            className="mt-3 text-[11px] font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1.5"
                        >
                            <RefreshCw size={12} /> Voltar ao que veio do cadastro
                        </button>

                        <button
                            onClick={() => setBibliotecaPara('')}
                            className="mt-2 text-[11px] font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1.5"
                        >
                            <ImageIcon size={12} /> Subir e organizar imagens
                        </button>
                    </div>

                    <div className="p-5 space-y-4">
                        {campos.map((c) => (
                            <div key={c.chave}>
                                {rotuloCampo(c)}

                                {c.tipo === 'imagem' ? (
                                    <div className="flex items-center gap-2">
                                        {/*
                                          Abre a BIBLIOTECA, nao o seletor de arquivo.

                                          Escolher e um clique numa miniatura do que ja foi
                                          enviado; subir arquivo novo continua la dentro. E o
                                          que faz a mesma foto servir de capa e de galeria sem
                                          virar dois objetos identicos no R2.
                                        */}
                                        <button
                                            onClick={() => setBibliotecaPara(c.chave)}
                                            className="flex items-center gap-2 px-3 py-2 text-xs font-semibold border border-slate-200 rounded-sm hover:bg-slate-50"
                                        >
                                            <ImageIcon size={13} />
                                            {valores[c.chave] ? 'Trocar' : 'Escolher imagem'}
                                        </button>
                                        {valores[c.chave] && (
                                            <>
                                                <img
                                                    src={`${CDN_ASSETS.replace(/\/+$/, '')}/${valores[c.chave]}`}
                                                    alt=""
                                                    className="w-10 h-10 object-cover rounded-sm border border-slate-200"
                                                />
                                                <button
                                                    onClick={() => definir(c.chave, '')}
                                                    className="text-[11px] text-slate-400 hover:text-red-600"
                                                >
                                                    tirar
                                                </button>
                                            </>
                                        )}
                                    </div>
                                ) : c.tipo === 'longo' ? (
                                    <textarea
                                        value={valores[c.chave] ?? ''}
                                        onChange={(e) => definir(c.chave, e.target.value)}
                                        rows={3}
                                        className={estiloInput}
                                    />
                                ) : (
                                    <input
                                        type={c.tipo === 'email' ? 'email' : 'text'}
                                        value={valores[c.chave] ?? ''}
                                        onChange={(e) => definir(c.chave, e.target.value)}
                                        className={estiloInput}
                                    />
                                )}
                            </div>
                        ))}

                        <p className="text-[11px] text-slate-400 leading-relaxed pt-2 border-t border-slate-100">
                            Campo deixado em branco não vira espaço vazio no site: a seção
                            inteira sai fora. Só o nome do negócio é indispensável.
                        </p>
                    </div>
                </div>

                {/* ---------------------------------------------------- prévia --- */}
                <div className="flex-1 bg-slate-100 min-w-0">
                    {/*
                      `sandbox` sem `allow-same-origin`: a prévia é conteúdo
                      renderizado, não precisa alcançar nada desta página.
                    */}
                    <iframe
                        srcDoc={html}
                        title="Prévia"
                        sandbox="allow-popups"
                        className="w-full h-full bg-white border-0"
                    />
                </div>
            </div>

            <input
                ref={arquivoRef}
                type="file"
                accept="image/*"
                onChange={aoEscolherArquivo}
                className="hidden"
            />

            {bibliotecaPara !== null && (
                <BibliotecaDeImagens
                    clienteId={clienteId}
                    campoAlvo={bibliotecaPara || null}
                    chaveAtual={bibliotecaPara ? valores[bibliotecaPara] : undefined}
                    aoEscolher={(chaveR2) => {
                        if (bibliotecaPara) definir(bibliotecaPara, chaveR2);
                        setBibliotecaPara(null);
                    }}
                    aoFechar={() => setBibliotecaPara(null)}
                />
            )}
        </div>
    );
};
